"use client";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";

export function VisibilityTracker() {
  const pathname = usePathname();

  useEffect(() => {
    const seen = new Set<string>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const name = (entry.target as HTMLElement).dataset.trackView;
          if (!name || seen.has(name)) continue;
          seen.add(name);
          pushEvent(analyticsEvents.element_view, {
            name,
            path: pathname ?? location.pathname,
          });
          io.unobserve(entry.target);
        }
      },
      { threshold: 0.5 },
    );

    const scan = () => {
      const els = document.querySelectorAll<HTMLElement>("[data-track-view]");
      for (const el of els) {
        const name = el.dataset.trackView;
        if (!name || seen.has(name)) continue;
        io.observe(el);
      }
    };
    scan();

    // Pick up elements that mount after first interaction (e.g. lazy
    // sticker grids, modals, expanded share rows).
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [pathname]);

  return null;
}
